import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import { IconButton } from "../icon-button/IconButton.js";

interface BaseMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  withIcon?: boolean;
  icon?: React.ReactElement | ((size?: number) => React.ReactElement);
  message: {
    primary: string;
    secondary?: string;
  };
  size?: "sm" | "md" | "lg";
  variant?: "warning" | "success" | "error" | "default" | "info";
  closable?: boolean;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  classNames?: {
    root?: string;
    iconWrapper?: string;
    contentWrapper?: string;
    closeIcon?: string;
    primary?: string;
    secondary?: string;
  };
}

const Message = React.forwardRef<HTMLDivElement, BaseMessageProps>(
  (
    {
      className,
      variant = "default",
      withIcon = true,
      closable = false,
      onClose,
      message,
      size = "sm",
      icon,
      classNames,
      ...rest
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = React.useState(true);

    const iconSize =
      size === "sm" ? 16 : size === "md" ? 20 : size === "lg" ? 20 : 16;

    const handleClose = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setIsVisible(false);
        if (onClose) {
          onClose(event);
        }
      },
      [onClose],
    );

    if (!isVisible) return;

    return (
      <div
        ref={ref}
        className={clsx(
          "ox-reset",
          "ox-MessageRoot",
          className,
          classNames?.root,
        )}
        {...rest}
        data-size={size}
        data-variant={variant}
      >
        {withIcon ? (
          <div
            className={clsx(
              "ox-reset",
              "ox-MessageIconWrapper",
              classNames?.iconWrapper,
            )}
          >
            &#x200B;
            {icon ? (
              typeof icon === "function" ? (
                icon(iconSize)
              ) : (
                icon
              )
            ) : variant === "warning" ? (
              <Warning size={iconSize} />
            ) : variant === "success" ? (
              <CheckCircle size={iconSize} />
            ) : variant === "error" ? (
              <WarningCircle size={iconSize} />
            ) : (
              <Info size={iconSize} />
            )}
          </div>
        ) : null}

        <div
          className={clsx(
            "ox-reset",
            "ox-MessageContentWrapper",
            classNames?.contentWrapper,
          )}
        >
          <p
            className={clsx(
              "ox-reset",
              "ox-MessageContent",
              size === "sm"
                ? "ox-fz-2xs"
                : size === "md"
                  ? "ox-fz-sm"
                  : size === "lg"
                    ? "ox-fz-md"
                    : "ox-fz-2xs",
            )}
          >
            <span
              className={clsx("ox-MessageContentPrimary", classNames?.primary)}
            >
              {message.primary}
            </span>
            {message.secondary && (
              <span
                className={clsx(
                  "ox-MessageContentSecondary",
                  classNames?.secondary,
                )}
              >
                {message.secondary}
              </span>
            )}
          </p>

          {closable && (
            <IconButton
              className={clsx("ox-MessageCloseButton", classNames?.closeIcon)}
              onClick={handleClose}
              variant="link"
              size="xs"
            >
              <X />
            </IconButton>
          )}
        </div>
      </div>
    );
  },
);
Message.displayName = "Message";
export { Message };
export type { BaseMessageProps };
